import { useState } from 'react';
import { proxy, useSnapshot } from 'valtio';

import { parseFen } from '@libs/chess/parseFen';
import { serializeFenBoard } from '@libs/chess/serializeFenBoard';
import { BoardPositions, PieceCode, SquareName } from '@libs/chess/types';

import { Props } from '../ChessBoard';

/*
    ____,-------------------------------,____
    \   |              Hook             |   /
    /___|-------------------------------|___\
*/

export type Args = {
  fen?: string;
  noValidate?: boolean;
  onPieceDrop?: (square: SquareName) => void;
  onChange?: (fen?: string) => void;
};

export type Value = {
  dragging: PieceCode | null;
  hasWhiteKing: boolean;
  hasBlackKing: boolean;
  onDragChange: (d: PieceCode | null) => void;

  props: MappedProps;
};

type MappedProps = Pick<
  Props,
  | 'position'
  | 'interactive'
  //
  | 'selectedFrom'
  //
  | 'onPieceDrag'
  | 'onSquarePointerDown'
  | 'onSquareDrop'
  | 'onDropOutside'
  | 'onSquarePlace'
  | 'onKeyDown'
>;

export function useSetupBoard({ fen, noValidate, onChange }: Args): Value {
  const [store] = useState(() => new SetupBoardStore(fen, onChange, noValidate));
  store.keepUpToDate(fen, onChange);
  const snapshot = useSnapshot(store.state);

  const props: MappedProps = {
    position: snapshot.positions ?? snapshot.fen,
    interactive: 'all',
    selectedFrom: snapshot.positions ? snapshot.selected : undefined,
    onPieceDrag: store.onPieceDrag,
    onSquarePointerDown: store.onSquarePointerDown,
    onSquareDrop: store.onSquareDrop,
    onDropOutside: store.onDropOutside,
    onSquarePlace: store.onSquarePlace,
  };

  return {
    dragging: snapshot.dragging,
    hasWhiteKing: snapshot.hasWhiteKing,
    hasBlackKing: snapshot.hasBlackKing,
    onDragChange: store.onDragChange,

    props,
  };
}

/*
    ____,-------------------------------,____
    \   |              Store            |   /
    /___|-------------------------------|___\
*/

class SetupBoardState {
  constructor(public fen?: string) {}
  selected?: SquareName;
  dragging: PieceCode | null = null;
  positions?: BoardPositions;

  get hasWhiteKing(): boolean {
    return this.hasPiece('wK');
  }
  get hasBlackKing(): boolean {
    return this.hasPiece('bK');
  }

  private hasPiece(p: PieceCode) {
    if (this.positions) {
      return Object.values(this.positions).includes(p);
    }

    if (this.fen) {
      const fp = p[0] === 'w' ? p[1]! : p[1]!.toUpperCase();
      return this.fen.includes(fp);
    }

    return false;
  }
}

class SetupBoardStore {
  constructor(
    fen?: string,
    private onChange?: (fen?: string) => void,
    private noValidate?: boolean,
  ) {
    this.state = proxy(new SetupBoardState(fen));
    this.state.positions = parseFen(fen)?.board;
    this.initialCastling = fen?.split(' ')[2] ?? 'KQkq';
  }

  state: SetupBoardState;
  private initialCastling: string;

  keepUpToDate = (fen?: string, onChange?: (fen?: string) => void) => {
    if (this.state.fen !== fen) {
      this.state.fen = fen;
      this.state.positions = parseFen(fen)?.board;
      this.state.selected = undefined;
      this.initialCastling = fen?.split(' ')[2] ?? 'KQkq';
    }
    this.onChange = onChange;
  };

  onPieceDrag = () => true;

  setSelected = (s?: SquareName) => {
    this.state.selected = s;
  };

  setDragging = (p: PieceCode | null) => {
    this.state.dragging = p;
  };

  onDragChange = (d: PieceCode | null) => {
    if (d) this.state.selected = undefined;
    this.state.dragging = d;
  };

  onSquarePointerDown = (s: SquareName, p: BoardPositions) => {
    this.state.selected = p[s] && s !== this.state.selected ? s : undefined;
  };

  onSquareDrop = (from: SquareName, to: SquareName, positions: BoardPositions) => {
    const piece = positions[from]!;

    if (!this.noValidate && !this.isValidPlacement(piece, to)) {
      return;
    }

    const nextPositions = { ...positions };
    nextPositions[from] = undefined;
    nextPositions[to] = piece;
    this.state.positions = nextPositions;
    this.state.selected = to;

    //

    const fen = this.createFen(this.state.positions, this.state.fen);
    this.state.fen = fen;
    this.onChange?.(fen);
  };

  onDropOutside = (from: SquareName, positions: BoardPositions) => {
    const nextPositions = { ...positions };
    nextPositions[from] = undefined;
    this.state.positions = nextPositions;
    this.state.selected = undefined;

    //

    const fen = this.createFen(this.state.positions, this.state.fen);
    this.state.fen = fen;
    this.onChange?.(fen);
  };

  onSquarePlace = (to: SquareName, piece: PieceCode, positions: BoardPositions) => {
    if (!this.noValidate && !this.isValidPlacement(piece, to)) {
      return;
    }

    const nextPositions = { ...positions };
    nextPositions[to] = piece;
    this.state.positions = nextPositions;
    this.state.selected = to;

    //

    const fen = this.createFen(this.state.positions, this.state.fen);
    this.state.fen = fen;
    this.onChange?.(fen);
  };

  private createFen(positions?: BoardPositions, initialFen?: string) {
    if (!positions) return undefined;

    const color = / b /.test(initialFen ?? '') ? 'b' : 'w';
    const castling = this.computeCastlingRights(positions);

    // Доску собираем сами: chess.js put() молча отбрасывает второго короля,
    // а редактору без валидации нужна любая расстановка
    return `${serializeFenBoard(positions)} ${color} ${castling} - 0 1`;
  }

  private computeCastlingRights(positions: BoardPositions): string {
    const initialCastling = this.initialCastling;

    let castling = '';

    // White kingside
    if (initialCastling.includes('K') && positions['e1'] === 'wK' && positions['h1'] === 'wR') {
      castling += 'K';
    }

    // White queenside
    if (initialCastling.includes('Q') && positions['e1'] === 'wK' && positions['a1'] === 'wR') {
      castling += 'Q';
    }

    // Black kingside (k)
    if (initialCastling.includes('k') && positions['e8'] === 'bK' && positions['h8'] === 'bR') {
      castling += 'k';
    }

    // Black queenside (q)
    if (initialCastling.includes('q') && positions['e8'] === 'bK' && positions['a8'] === 'bR') {
      castling += 'q';
    }

    return castling || '-';
  }

  private isValidPlacement(piece: PieceCode, to: SquareName) {
    // пешки в крайних рядах (chess.js не может с ними работать)
    return !((piece === 'wP' || piece === 'bP') && (to[1] === '1' || to[1] === '8'));
  }
}
